/*
  Yappa+ New-In feature — client-side behavior.

  Responsibilities:
    1. Countdown chip: tick every second, update visible time remaining.
    2. On expiry: swap countdown chip into a "Recently added" chip in place,
       unlock any ATC gates on the same product, dispatch a `yappa-plus:expired`
       event for downstream listeners.
    3. Modal: open on "(i)" click on countdown or on click of the ATC gate
       link, close on backdrop / X / Escape.

  Runs once on DOMContentLoaded. Also re-scans after `page:loaded` and
  `collection:reloaded` CONCEPT theme events so cards injected by ajax
  filtering/sorting get their timers wired up.
*/
(function () {
  'use strict';

  var TICK_MS = 1000;
  var tickers = new Map();
  var tickerInterval = null;

  function pad(n) { return n < 10 ? '0' + n : String(n); }

  function formatRemaining(msLeft) {
    if (msLeft <= 0) return '00:00:00';
    var totalSec = Math.floor(msLeft / 1000);
    var days = Math.floor(totalSec / 86400);
    var hours = Math.floor((totalSec % 86400) / 3600);
    var mins = Math.floor((totalSec % 3600) / 60);
    var secs = totalSec % 60;
    if (days > 0) {
      return days + 'd ' + pad(hours) + ':' + pad(mins) + ':' + pad(secs);
    }
    return pad(hours) + ':' + pad(mins) + ':' + pad(secs);
  }

  function expire(chip) {
    var recentLabel = chip.getAttribute('data-yp-recent-label') || 'Recently added';
    chip.classList.remove('yp-chip--exclusive', 'yp-chip--member');
    chip.classList.add('yp-chip--recent');
    var timerEl = chip.querySelector('[data-yp-timer]');
    var labelEl = chip.querySelector('[data-yp-label]');
    var infoEl = chip.querySelector('[data-yp-info]');
    if (timerEl) timerEl.remove();
    if (infoEl) infoEl.remove();
    if (labelEl) labelEl.textContent = recentLabel;
    chip.setAttribute('data-yp-expired', 'true');

    var productHandle = chip.getAttribute('data-yp-product');
    if (productHandle) {
      document.querySelectorAll('.yp-atc-lock[data-yp-product="' + CSS.escape(productHandle) + '"]').forEach(function (lock) {
        lock.setAttribute('data-yp-locked', 'false');
        var notice = lock.querySelector('.yp-atc-lock__notice');
        if (notice) notice.style.display = 'none';
      });
      document.dispatchEvent(new CustomEvent('yappa-plus:expired', {
        detail: { productHandle: productHandle }
      }));
    }
  }

  function tickAll() {
    var now = Date.now();
    tickers.forEach(function (info, chip) {
      var msLeft = info.expiresAt - now;
      if (msLeft <= 0) {
        expire(chip);
        tickers.delete(chip);
        return;
      }
      if (info.timerEl) {
        info.timerEl.textContent = formatRemaining(msLeft);
      }
    });
    if (tickers.size === 0 && tickerInterval) {
      clearInterval(tickerInterval);
      tickerInterval = null;
    }
  }

  function ensureTickerRunning() {
    if (tickerInterval) return;
    tickerInterval = setInterval(tickAll, TICK_MS);
    tickAll();
  }

  function registerChip(chip) {
    if (tickers.has(chip)) return;
    var expiresRaw = chip.getAttribute('data-yp-expires-at');
    if (!expiresRaw) return;
    // Preferred: unix seconds (integer) — emitted by yappa-plus-vars.
    // This is TZ-neutral. Fallback: ISO string via Date.parse for any
    // legacy caller that still sends one.
    var expiresAt;
    var asNum = Number(expiresRaw);
    if (!isNaN(asNum) && asNum > 1000000000) {
      expiresAt = asNum * 1000;
    } else {
      expiresAt = Date.parse(expiresRaw);
    }
    if (isNaN(expiresAt)) return;
    var timerEl = chip.querySelector('[data-yp-timer]');
    if (expiresAt - Date.now() <= 0) {
      expire(chip);
      return;
    }
    tickers.set(chip, { expiresAt: expiresAt, timerEl: timerEl });
    ensureTickerRunning();
  }

  function scanChips(root) {
    (root || document).querySelectorAll('[data-yp-countdown]').forEach(registerChip);
  }

  // Modal open/close — routes to modals by "kind" attribute.
  // Multiple modals can coexist. `data-yp-modal-open` picks the target;
  // legacy triggers without a value default to the "upsell" (New-In) modal.
  function getModal(kind) {
    return document.querySelector('[data-yp-modal="' + kind + '"]');
  }
  function getAllModals() {
    return document.querySelectorAll('[data-yp-modal]');
  }

  // A trigger is "product context" when it lives inside a product
  // card, wishlist card, PDP product-info, ATC lock notice, or one of
  // our own countdown/badge chips — i.e. anywhere shopping a specific
  // product. Non-product surfaces (homepage buttons, menu items, hub
  // sections) are NOT product context, so the modal's wishlist note
  // ("You can still add this to your wishlist…") is meaningless there
  // and gets hidden.
  var PRODUCT_CONTEXT_SEL = [
    '.product-card',
    '.horizontal-product',
    'product-info',
    '.yp-atc-lock',
    '.yp-chip-overlay',
    '.yp-card-overlay',
    '.yp-pdp-badges',
    '[data-yp-countdown]'
  ].join(',');

  function isProductContext(trigger) {
    return !!(trigger && trigger.closest && trigger.closest(PRODUCT_CONTEXT_SEL));
  }

  function openModal(kind, trigger) {
    var modal = getModal(kind);
    if (!modal) return;
    // Close any other open modal first — only one at a time.
    getAllModals().forEach(function (m) {
      if (m !== modal && m.getAttribute('data-yp-open') === 'true') {
        m.setAttribute('data-yp-open', 'false');
        m.setAttribute('aria-hidden', 'true');
      }
    });
    // Toggle context — CSS uses [data-yp-context] to show/hide the
    // wishlist reassurance note on the Upsell modal.
    modal.setAttribute('data-yp-context', isProductContext(trigger) ? 'product' : 'generic');
    modal.setAttribute('data-yp-open', 'true');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    var closeBtn = modal.querySelector('[data-yp-modal-close]');
    if (closeBtn) closeBtn.focus();
  }

  function closeAllModals() {
    getAllModals().forEach(function (modal) {
      modal.setAttribute('data-yp-open', 'false');
      modal.setAttribute('aria-hidden', 'true');
    });
    document.body.style.overflow = '';
  }

  document.addEventListener('click', function (evt) {
    var trigger = evt.target.closest('[data-yp-modal-open]');
    if (trigger) {
      evt.preventDefault();
      var kind = trigger.getAttribute('data-yp-modal-open') || 'upsell';
      openModal(kind, trigger);
      return;
    }
    // Merchant-friendly trigger: any <a href="#yp-modal-<kind>"> (or
    // any element whose closest link href matches) opens the modal
    // and suppresses the # navigation. Lets the theme's Button block
    // — which only exposes a URL field — drive our modals with a
    // plain link value like `#yp-modal-credits` or `#yp-modal-upsell`.
    var linkTrigger = evt.target.closest('a[href^="#yp-modal-"]');
    if (linkTrigger) {
      var href = linkTrigger.getAttribute('href') || '';
      var linkKind = href.replace('#yp-modal-', '').trim() || 'upsell';
      evt.preventDefault();
      openModal(linkKind, linkTrigger);
      return;
    }
    var closer = evt.target.closest('[data-yp-modal-close], [data-yp-modal-backdrop]');
    if (closer) {
      evt.preventDefault();
      closeAllModals();
    }
  });

  document.addEventListener('keydown', function (evt) {
    if (evt.key === 'Escape') closeAllModals();
  });

  // Overlay merchant-editable content onto a modal. Each
  // <script data-yp-modal-content="<kind>"> targets the modal
  // with the matching data-yp-modal="<kind>".
  function applyMerchantModalContent(kind) {
    var script = document.querySelector('script[data-yp-modal-content="' + kind + '"]');
    var modal = getModal(kind);
    if (!script || !modal) return;
    var data;
    try { data = JSON.parse(script.textContent.trim()); } catch (e) { return; }
    if (!data) return;
    var setText = function (sel, val) {
      if (val == null) return;
      var el = modal.querySelector(sel);
      if (el) el.textContent = val;
    };
    var setBullets = function (sel, raw) {
      if (!raw) return;
      var ul = modal.querySelector(sel);
      if (!ul) return;
      ul.innerHTML = '';
      String(raw).split(/\r?\n/).forEach(function (line) {
        var t = line.trim();
        if (!t) return;
        var li = document.createElement('li');
        li.className = 'yp-modal__bullet';
        li.textContent = t;
        ul.appendChild(li);
      });
    };
    setText('.yp-modal__heading', data.heading);
    setText('.yp-modal__sub', data.sub);
    setBullets('.yp-modal__bullets', data.bullets);
    setText('.yp-modal__earn-heading', data.earn_heading);
    setBullets('.yp-modal__earn-bullets', data.earn_bullets);
    setText('.yp-modal__redeem-heading', data.redeem_heading);
    setBullets('.yp-modal__redeem-bullets', data.redeem_bullets);
    setText('.yp-modal__wishlist-note', data.wishlist_note);
    var cta = modal.querySelector('.yp-modal__cta');
    if (cta) {
      if (data.cta_label) cta.textContent = data.cta_label;
      if (data.cta_url) cta.setAttribute('href', data.cta_url);
    }
    // Banner image is a CSS background on .yp-modal__banner — no <img>.
    // Yappa perf: store the URL as a CSS custom property instead of
    // writing `background-image` directly. The `background-image:
    // var(--yp-modal-banner-url)` rule in yappa-plus.css is gated on
    // `.yp-modal[data-yp-open="true"]`, so the browser only resolves
    // the url() — and fires the GET — on the first open. Previously
    // setting .style.backgroundImage here at DOM-ready pulled ~115 KB
    // of modal banner webps on every PLP, even when the modals were
    // visibility:hidden and never opened.
    if (data.image) {
      var banner = modal.querySelector('[data-yp-banner]');
      if (banner) {
        banner.style.setProperty('--yp-modal-banner-url', "url('" + String(data.image).replace(/'/g, "%27") + "')");
      }
    }
    // Optional per-section logo override (image_picker → asset URL).
    if (data.logo) {
      var logo = modal.querySelector('[data-yp-logo]');
      if (logo) logo.setAttribute('src', data.logo);
    }
  }

  function init() {
    scanChips(document);
    applyMerchantModalContent('upsell');
    applyMerchantModalContent('credits');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('page:loaded', function () { scanChips(document); });
  document.addEventListener('collection:reloaded', function () { scanChips(document); });
  document.addEventListener('quickview:loaded', function () { scanChips(document); });

  // Yappa: dynamic-insertion catch-all. RecentlyViewed and the
  // wishlist tab AJAX-inject product-card HTML long after
  // DOMContentLoaded, and their CustomEvent dispatches don't bubble
  // (RecentlyViewed dispatches `recentlyViewed:loaded` on the custom
  // element without `bubbles: true`), so document-level listeners
  // never fire. Result: countdown chips in Recently Viewed cards had
  // no timer text ("...").
  //
  // MutationObserver watches the whole document for newly-added
  // subtrees containing `[data-yp-countdown]` and registers each one.
  // Cheap in practice — the observer only fires on childList changes,
  // and we early-return for additions with no matching chips.
  if ('MutationObserver' in window) {
    var chipObserver = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue; // element nodes only
          if (node.matches && node.matches('[data-yp-countdown]')) {
            registerChip(node);
          } else if (node.querySelectorAll) {
            scanChips(node);
          }
        }
      }
    });
    chipObserver.observe(document.body, { childList: true, subtree: true });
  }
})();
